import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import PoweredByFooter from '../shared/PoweredByFooter';

const ReminderEmailDialog = ({ open, onClose, deliverableId }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    to: '', cc: '', bcc: '', display_name: '', reply_to: '', subject: '', body: '',
    from_email: 'mailbox@mygreendoorapp.com',
  });

  useEffect(() => {
    if (open && deliverableId) fetchDraft();
  }, [open, deliverableId]);

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/deliverable-reminders/email-draft/${deliverableId}`);
      setForm(prev => ({
        ...prev,
        to: res.data.to || '',
        cc: res.data.cc || '',
        bcc: res.data.bcc || '',
        display_name: res.data.display_name || '',
        reply_to: res.data.reply_to || '',
        subject: res.data.subject || '',
        body: res.data.body || '',
        from_email: res.data.from_email || 'mailbox@mygreendoorapp.com',
      }));
    } catch { toast.error('Failed to load reminder email draft'); }
    finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!form.to.trim()) { toast.error('Recipient email is required'); return; }
    if (!form.subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      await api.post(`/deliverable-reminders/send-email/${deliverableId}`, {
        to: form.to, cc: form.cc, bcc: form.bcc,
        display_name: form.display_name, reply_to: form.reply_to,
        subject: form.subject, body: form.body,
      });
      toast.success('Reminder email sent');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder email');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Send Reminder Email</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="font-semibold text-right">To</Label>
              <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="recipient@example.com" />
            </div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="text-right text-muted-foreground">CC</Label>
              <Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} placeholder="cc@example.com" />
            </div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="text-right text-muted-foreground">BCC</Label>
              <Input value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })} placeholder="bcc@example.com" />
            </div>
            <div className="border rounded-md p-3 space-y-2 bg-muted/20">
              <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                <Label className="font-semibold text-right">From</Label>
                <span className="text-sm text-muted-foreground">{form.from_email}</span>
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4">
                <Label className="text-right text-sm text-muted-foreground">Display Name</Label>
                <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="h-8" />
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4">
                <Label className="text-right text-sm text-muted-foreground">Reply To</Label>
                <Input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="h-8" />
              </div>
            </div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="font-semibold text-right">RE:</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </div>
            <RichTextEditor value={form.body} onChange={(value) => setForm({ ...form, body: value })} />
            <PoweredByFooter />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || loading} className="bg-primary hover:bg-primary/90 text-white">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderEmailDialog;
